import { motion } from "framer-motion";
import { Briefcase, Check, Clock, X } from "lucide-react";
import type { FunctionCategory, Teammate } from "@/lib/types";
import { cn } from "@/lib/cn";

const PRACTICE_LABEL: Record<FunctionCategory, string> = {
  corporate_ma: "Corporate / M&A",
  litigation: "Dispute Resolution",
  tax: "Tax",
  labour: "Employment",
  real_estate: "Real Estate",
  banking_finance: "Banking & Finance",
  ip: "IP",
  competition: "Competition",
  tmt: "TMT",
  public_law: "Public Law",
  restructuring: "Restructuring",
  compliance: "Compliance",
  other: "Other",
};

type Props = {
  teammate: Teammate;
  compact?: boolean;
  className?: string;
};

export function TeammateCard({ teammate, compact, className }: Props) {
  const accent = teammate.accent || "#5EEAFF";
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className={cn(
        "relative overflow-hidden rounded-xl p-6 glass glow-border",
        className,
      )}
    >
      <div
        aria-hidden
        className="absolute -top-24 -right-24 h-64 w-64 rounded-full opacity-30 blur-3xl"
        style={{ background: `radial-gradient(circle, ${accent}, transparent 70%)` }}
      />
      <div className="relative">
        <div className="flex items-start gap-4">
          <div
            className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-lg text-3xl"
            style={{ background: `linear-gradient(135deg, ${accent}33, ${accent}10)` }}
          >
            {teammate.emoji || "✨"}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs uppercase tracking-widest text-white/50">
              {teammate.creatorName ? `${teammate.creatorName}'s AI associate` : "Your AI associate"}
            </div>
            <h3 className="mt-1 text-2xl font-semibold leading-tight text-white">
              {teammate.name}
            </h3>
            <p className="mt-1 flex items-center gap-1.5 text-sm text-white/70">
              <Briefcase className="h-3.5 w-3.5" />
              <span>{PRACTICE_LABEL[teammate.function] || teammate.function}</span>
              <span className="opacity-40">·</span>
              <span>{teammate.role}</span>
            </p>
          </div>
          {teammate.hoursSaved > 0 && (
            <div
              className="flex flex-col items-center justify-center rounded-lg px-3 py-2 text-center"
              style={{
                background: `linear-gradient(135deg, ${accent}33, ${accent}10)`,
                border: `1px solid ${accent}44`,
              }}
            >
              <Clock className="h-4 w-4" style={{ color: accent }} />
              <div className="mt-1 text-2xl font-semibold leading-none text-white">
                +{teammate.hoursSaved}
              </div>
              <div className="mt-0.5 text-[9px] uppercase tracking-widest text-white/60">
                h / week
              </div>
            </div>
          )}
        </div>

        <p className="mt-5 text-base leading-relaxed text-white/90">{teammate.mission}</p>

        {!compact && (
          <>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <Block label="What it does" color="#5EEAFF" icon={<Check className="h-3.5 w-3.5" />}>
                <ul className="space-y-1.5 text-sm text-white/80">
                  {teammate.does.map((d, i) => (
                    <li key={i} className="leading-snug">{d}</li>
                  ))}
                </ul>
              </Block>
              <Block label="Stays human" color="#FF9F6A" icon={<X className="h-3.5 w-3.5" />}>
                <ul className="space-y-1.5 text-sm text-white/80">
                  {teammate.notDoes.map((d, i) => (
                    <li key={i} className="leading-snug">{d}</li>
                  ))}
                </ul>
              </Block>
            </div>

            <Block label="Reusable prompt" color="#8C9BFF" className="mt-4">
              <pre className="whitespace-pre-wrap text-sm leading-relaxed text-white/85 font-sans">
                {teammate.prompt}
              </pre>
            </Block>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <Block label="Next automation step" color="#FFC36A">
                <p className="text-sm leading-relaxed text-white/85">{teammate.automation}</p>
              </Block>
              <Block label="Your 2-week plan" color="#A4F36F">
                <ol className="space-y-1.5 text-sm text-white/85">
                  {teammate.plan.map((p, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-white/40">{i + 1}.</span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ol>
              </Block>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
}

function Block({
  label,
  color,
  icon,
  children,
  className,
}: {
  label: string;
  color: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn("rounded-lg p-4", className)}
      style={{
        background: `linear-gradient(180deg, ${color}10, ${color}04)`,
        border: `1px solid ${color}22`,
      }}
    >
      <div
        className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest"
        style={{ color }}
      >
        {icon}
        {label}
      </div>
      {children}
    </div>
  );
}
